/**
 * For multicolumn carousels, hide slides outside the visible viewport from
 * screen readers. Optionally moves focus to the first visible slide so
 * VoiceOver follows after user-triggered navigation.
 * @param {object} flkty       - Flickity instance
 * @param {boolean} moveFocus  - Whether to focus the first visible slide
 */
function updateMulticolumnSlideAria(flkty, moveFocus) {
    const viewport = flkty.element.querySelector(".flickity-viewport");
    if (!viewport) return;
    const vpRect = viewport.getBoundingClientRect();
    let firstVisible = null;
    flkty.cells.forEach((cell) => {
        // Slides with .hidden are duplicate content rendered above the carousel —
        // always exclude them regardless of their visual position.
        const isDuplicate = cell.element.classList.contains("hidden");
        const cellRect = cell.element.getBoundingClientRect();
        const isVisible =
            !isDuplicate &&
            cellRect.right > vpRect.left &&
            cellRect.left < vpRect.right;
        cell.element.setAttribute("aria-hidden", String(!isVisible));
        cell.element.setAttribute("tabindex", isVisible ? "0" : "-1");
        if (isVisible && !firstVisible) firstVisible = cell.element;
    });
    if (moveFocus && firstVisible) firstVisible.focus();
}

/**
 *  @class
 *  @function SlideShow
 */
if (!customElements.get("slide-show")) {
    class SlideShow extends HTMLElement {
        constructor() {
            super();
            const slideshow = this;
            const slideshowParent = slideshow.closest(".slideshow-parent");

            let dots = slideshow.dataset.dots === "true",
                slideshow_slides = Array.from(
                    slideshow.querySelectorAll(".carousel__slide"),
                ),
                autoplay =
                    slideshow.dataset.autoplay == "false"
                        ? false
                        : parseInt(slideshow.dataset.autoplay, 10),
                align = slideshow.dataset.align == "center" ? "center" : "left",
                fade = slideshow.dataset.fade == "true" ? true : false,
                prev_button = slideshowParent
                    ? slideshowParent.querySelector(".flickity-prev")
                    : slideshow.querySelector(".flickity-prev"),
                next_button = slideshowParent
                    ? slideshowParent.querySelector(".flickity-next")
                    : slideshow.querySelector(".flickity-next"),
                custom_dots = slideshowParent
                    ? slideshowParent.querySelector(".flickity-page-dots")
                    : slideshow.querySelector(".flickity-page-dots"),
                progress_bar = slideshow.parentNode.querySelector(
                    ".flickity-progress--bar",
                ),
                animations = [],
                rightToLeft = document.dir === "rtl",
                isAnnouncementBar = !!slideshow.closest(".announcement-bar"),
                animations_enabled =
                    document.body.classList.contains("animations-true") &&
                    typeof gsap !== "undefined",
                selectedIndex = 0,
                args = {
                    wrapAround: true,
                    cellAlign: align,
                    pageDots: false,
                    contain: true,
                    fade: fade,
                    autoPlay: autoplay,
                    rightToLeft: rightToLeft,
                    prevNextButtons: false,
                    cellSelector: ".carousel__slide",
                    on: {},
                };

            if (slideshow_slides.length <= 1) {
                args.wrapAround = false;
            }

            if (slideshow.classList.contains("testimonials__carousel")) {
                // These pagination buttons exist within a higher level parent.
                prev_button = slideshowParent
                    .closest(".testimonials")
                    .querySelector(".testimonials__navigation .flickity-prev");
                next_button = slideshowParent
                    .closest(".testimonials")
                    .querySelector(".testimonials__navigation .flickity-next");
            }

            if (slideshow_slides.length < 1) {
                return;
            }
            if (
                slideshow.classList.contains("images-loaded") ||
                (slideshow.classList.contains("main-slideshow") &&
                    slideshow_slides.length === 1)
            ) {
                args.imagesLoaded = true;
            }
            if (
                slideshow.classList.contains("image-with-text-slideshow__image")
            ) {
                let main_slideshow = slideshow.parentNode.querySelector(
                        ".image-with-text-slideshow__content",
                    ),
                    image_slideshow_slides = slideshow.querySelectorAll(
                        ".image-with-text-slideshow__image-media",
                    );
                args.draggable = false;
                args.asNavFor = main_slideshow;

                if (image_slideshow_slides.length) {
                    if (
                        image_slideshow_slides[0].classList.contains(
                            "desktop-height-auto",
                        )
                    ) {
                        args.adaptiveHeight = true;
                    }
                }
            }
            if (slideshow.classList.contains("customer-reviews__image")) {
                let main_slideshow = slideshow.parentNode.querySelector(
                    ".customer-reviews__content",
                );
                args.draggable = false;
                args.asNavFor = main_slideshow;
            }
            if (
                slideshow.classList.contains(
                    "image-with-text-slideshow__content",
                ) ||
                slideshow.classList.contains("testimonials__carousel") ||
                slideshow.classList.contains("customer-reviews__content") ||
                slideshow.classList.contains("customer-reviews__image")
            ) {
                args.adaptiveHeight = true;
            }
            // Disable dragging for multicolumn carousel with 4 or fewer items (desktop only)
            if (
                slideshow.dataset.slideCount &&
                parseInt(slideshow.dataset.slideCount, 10) <= 4 &&
                window.innerWidth > 767
            ) {
                args.draggable = false;
            }
            // Multicolumn carousel: prevent Flickity from adding tabindex to the
            // track, manage aria-hidden on off-screen slides, and move focus to the
            // first visible slide after user-triggered navigation so VoiceOver follows.
            if (slideshow.dataset.slideCount) {
                args.accessibility = false;
                let userNavigated = false;
                if (prev_button) {
                    prev_button.addEventListener("click", () => {
                        userNavigated = true;
                    });
                }
                if (next_button) {
                    next_button.addEventListener("click", () => {
                        userNavigated = true;
                    });
                }
                args.on.ready = function () {
                    updateMulticolumnSlideAria(this, false);
                };
                args.on.settle = function () {
                    updateMulticolumnSlideAria(this, userNavigated);
                    userNavigated = false;
                };
            }
            if (slideshow.classList.contains("custom-dots")) {
                if (
                    animations_enabled &&
                    slideshow.classList.contains("main-slideshow")
                ) {
                    this.prepareAnimations(slideshow, animations);
                }
                args.pauseAutoPlayOnHover = false;

                args.on = {
                    staticClick: function () {
                        this.unpausePlayer();
                    },
                    ready: function () {
                        let flkty = this;
                        // Animations.
                        if (
                            animations_enabled &&
                            slideshow.classList.contains("main-slideshow")
                        ) {
                            slideshow.animateSlides(0, animations);
                            gsap.set(
                                slideshow.querySelectorAll(
                                    ".subheading,.split-text,.button",
                                ),
                                { visibility: "visible" },
                            );
                        }

                        // Custom Dots.
                        if (dots && custom_dots) {
                            let dots = custom_dots.querySelectorAll("li");
                            dots.forEach((dot, i) => {
                                dot.addEventListener("click", (e) => {
                                    flkty.select(i);
                                });
                                dot.addEventListener("keydown", (e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        flkty.select(i);
                                    }
                                });
                                dot.setAttribute("tabindex", "0");
                            });
                            dots.forEach((dot) =>
                                dot.setAttribute("aria-selected", "false"),
                            );
                            dots[this.selectedIndex].classList.add(
                                "is-selected",
                            );
                            dots[this.selectedIndex].setAttribute(
                                "aria-selected",
                                "true",
                            );
                        }
                        document.fonts.ready.then(function () {
                            flkty.resize();
                        });

                        // Video Support.
                        let video_container =
                            flkty.cells[0].element.querySelector(
                                ".slideshow__slide-video-bg",
                            );
                        if (video_container) {
                            if (video_container.querySelector("iframe")) {
                                video_container.querySelector("iframe").onload =
                                    function () {
                                        slideshow.videoPlay(video_container);
                                    };
                            } else if (video_container.querySelector("video")) {
                                video_container.querySelector(
                                    "video",
                                ).onloadstart = function () {
                                    slideshow.videoPlay(video_container);
                                };
                            }
                        }
                    },
                    change: function (index) {
                        let previousIndex = fizzyUIUtils.modulo(
                            this.selectedIndex - 1,
                            this.slides.length,
                        );

                        // Animations.
                        if (
                            animations_enabled &&
                            slideshow.classList.contains("main-slideshow")
                        ) {
                            setTimeout(() => {
                                slideshow.animateReverse(
                                    previousIndex,
                                    animations,
                                );
                            }, 300);
                            slideshow.animateSlides(index, animations);
                        }

                        // Custom Dots.
                        if (dots && custom_dots) {
                            let dots = custom_dots.querySelectorAll("li");
                            dots.forEach((dot, i) => {
                                dot.classList.remove("is-selected");
                                dot.setAttribute("aria-selected", "false");
                            });
                            dots[this.selectedIndex].classList.add(
                                "is-selected",
                            );
                            dots[this.selectedIndex].setAttribute(
                                "aria-selected",
                                "true",
                            );
                        }

                        // AutoPlay
                        if (autoplay) {
                            this.stopPlayer();
                            this.playPlayer();
                        }

                        // Video Support.
                        // previous slide
                        let video_container_prev = flkty.cells[
                            previousIndex
                        ].element.querySelector(".slideshow__slide-video-bg");
                        if (video_container_prev) {
                            slideshow.videoPause(video_container_prev);
                        }
                        // current slide
                        let video_container = flkty.cells[
                            index
                        ].element.querySelector(".slideshow__slide-video-bg");
                        if (video_container) {
                            if (video_container.querySelector("iframe")) {
                                if (
                                    video_container
                                        .querySelector("iframe")
                                        .classList.contains("lazyload")
                                ) {
                                    video_container
                                        .querySelector("iframe")
                                        .addEventListener(
                                            "lazybeforeunveil",
                                            slideshow.videoPlay(
                                                video_container,
                                            ),
                                        );
                                    lazySizes.loader.checkElems();
                                } else {
                                    slideshow.videoPlay(video_container);
                                }
                            } else if (video_container.querySelector("video")) {
                                slideshow.videoPlay(video_container);
                            }
                        }
                    },
                };
            }
            if (slideshow.classList.contains("main-slideshow")) {
                if (
                    slideshow.classList.contains("desktop-height-image") ||
                    slideshow.classList.contains("mobile-height-image")
                ) {
                    args.adaptiveHeight = true;
                }
            }
            if (slideshow.classList.contains("products")) {
                args.wrapAround = false;
                args.on.ready = function () {
                    var flickity = this;
                    if (next_button) {
                        window.addEventListener("resize", function () {
                            slideshow.centerArrows(
                                flickity,
                                prev_button,
                                next_button,
                            );
                        });
                    }
                    window.dispatchEvent(new Event("resize"));
                };
            }
            if (progress_bar) {
                args.wrapAround = false;
                args.on.scroll = function (progress) {
                    progress = Math.max(0, Math.min(1, progress));

                    progress_bar.style.width = progress * 100 + "%";
                };
            }
            // Disable Flickity's built-in accessibility (tabindex on container)
            // for the announcement bar — we implement the full ARIA carousel pattern manually.
            if (isAnnouncementBar) {
                args.accessibility = false;
            }

            const flkty = new Flickity(slideshow, args);

            selectedIndex = flkty.selectedIndex;

            slideshow.dataset.initiated = true;

            // Announcement bar: full ARIA carousel pattern.
            // Keeps only the active slide visible to AT and focusable.
            if (isAnnouncementBar) {
                // Use flkty.cells (originals only) so Flickity's wrapAround
                // clones — which share the same .carousel__slide class — don't
                // corrupt the index mapping.
                const updateAnnouncementVisibility = () => {
                    flkty.cells.forEach((cell, i) => {
                        const slide = cell.element;
                        const isSelected = i === flkty.selectedIndex;
                        slide.setAttribute(
                            "aria-hidden",
                            isSelected ? "false" : "true",
                        );
                        slide.querySelectorAll("a, button").forEach((el) => {
                            if (isSelected) {
                                el.removeAttribute("tabindex");
                            } else {
                                el.setAttribute("tabindex", "-1");
                            }
                        });
                    });
                };
                updateAnnouncementVisibility();
                flkty.on("change", updateAnnouncementVisibility);

                // Permanently hide wrapAround clones from AT — they are purely
                // visual duplicates and should never be read or focused.
                const originalElements = new Set(
                    flkty.cells.map((c) => c.element),
                );
                slideshow.querySelectorAll(".carousel__slide").forEach((el) => {
                    if (!originalElements.has(el)) {
                        el.setAttribute("aria-hidden", "true");
                        el.querySelectorAll("a, button").forEach((el) => {
                            el.setAttribute("tabindex", "-1");
                        });
                    }
                });

                // WCAG 2.2.2: pause autoplay when keyboard focus enters the
                // component so users have time to interact with slide links.
                // Resume only when focus leaves the entire component.
                slideshow.addEventListener("focusin", () => {
                    flkty.stopPlayer();
                });
                slideshow.addEventListener("focusout", (e) => {
                    if (!slideshow.contains(e.relatedTarget) && autoplay) {
                        flkty.playPlayer();
                    }
                });
            }

            // Handle resize for multicolumn carousel with few slides
            if (slideshow.dataset.slideCount) {
                let resizeTimer;
                window.addEventListener(
                    "resize",
                    () => {
                        clearTimeout(resizeTimer);
                        resizeTimer = setTimeout(() => {
                            const slideCount = parseInt(
                                slideshow.dataset.slideCount,
                                10,
                            );
                            const isDesktop = window.innerWidth > 767;
                            const shouldDisableDrag =
                                slideCount <= 4 && isDesktop;

                            // Update draggable option
                            if (
                                shouldDisableDrag &&
                                flkty.options.draggable !== false
                            ) {
                                flkty.options.draggable = false;
                                flkty.updateDraggable();
                            } else if (
                                !shouldDisableDrag &&
                                flkty.options.draggable !== true
                            ) {
                                flkty.options.draggable = true;
                                flkty.updateDraggable();
                            }
                        }, 250);
                    },
                    { passive: true },
                );
            }

            // On resize, revert the text split and clear animations so text renders naturally
            if (
                animations_enabled &&
                slideshow.classList.contains("main-slideshow")
            ) {
                let animResizeTimer;
                window.addEventListener(
                    "resize",
                    () => {
                        clearTimeout(animResizeTimer);
                        animResizeTimer = setTimeout(() => {
                            if (slideshow.splitTextInstance) {
                                animations.forEach((tl) => tl && tl.kill());
                                gsap.set(
                                    slideshow.querySelectorAll(
                                        "h1, p:not(.subheading), .button",
                                    ),
                                    {
                                        clearProps:
                                            "y,yPercent,opacity,clipPath",
                                    },
                                );
                                gsap.set(
                                    slideshow.querySelectorAll(
                                        ".subheading, .split-text, .button",
                                    ),
                                    { visibility: "visible" },
                                );
                                slideshow.splitTextInstance.revert();
                                slideshow.splitTextInstance = null;
                            }
                        }, 250);
                    },
                    { passive: true },
                );
            }

            if (prev_button) {
                prev_button.addEventListener("click", (event) => {
                    flkty.previous();
                });
                next_button.addEventListener("click", (event) => {
                    flkty.next();
                });
            }
            if (Shopify.designMode) {
                slideshow.addEventListener("shopify:block:select", (event) => {
                    let index = slideshow_slides.indexOf(event.target);
                    flkty.select(index);
                });
            }
        }
        videoPause(video_container) {
            setTimeout(() => {
                if (video_container.dataset.provider === "hosted") {
                    video_container.querySelector("video").pause();
                } else if (video_container.dataset.provider === "youtube") {
                    video_container
                        .querySelector("iframe")
                        .contentWindow.postMessage(
                            JSON.stringify({
                                event: "command",
                                func: "pauseVideo",
                                args: "",
                            }),
                            "*",
                        );
                } else if (video_container.dataset.provider === "vimeo") {
                    video_container
                        .querySelector("iframe")
                        .contentWindow.postMessage(
                            JSON.stringify({
                                method: "pause",
                            }),
                            "*",
                        );
                }
            }, 10);
        }
        videoPlay(video_container) {
            setTimeout(() => {
                if (video_container.dataset.provider === "hosted") {
                    video_container.querySelector("video").play();
                } else if (video_container.dataset.provider === "youtube") {
                    video_container
                        .querySelector("iframe")
                        .contentWindow.postMessage(
                            JSON.stringify({
                                event: "command",
                                func: "playVideo",
                                args: "",
                            }),
                            "*",
                        );
                } else if (video_container.dataset.provider === "vimeo") {
                    video_container
                        .querySelector("iframe")
                        .contentWindow.postMessage(
                            JSON.stringify({
                                method: "play",
                            }),
                            "*",
                        );
                }
            }, 10);
        }
        prepareAnimations(slideshow, animations) {
            if (!slideshow.dataset.animationsReady) {
                slideshow.splitTextInstance = new SplitText(
                    slideshow.querySelectorAll("h1, p:not(.subheading)"),
                    {
                        type: "lines, words",
                        linesClass: "line-child",
                    },
                );
                slideshow
                    .querySelectorAll(".slideshow__slide")
                    .forEach((item, i) => {
                        let tl = gsap.timeline({
                                paused: true,
                            }),
                            button_offset = 0;

                        animations[i] = tl;

                        if (slideshow.dataset.transition == "swipe") {
                            tl.to(
                                item,
                                {
                                    duration: item.classList.contains(
                                        "is-initial-selected",
                                    )
                                        ? 0.2
                                        : 0.7,
                                    clipPath:
                                        "polygon(100% 0, 0 0, 0 100%, 100% 100%)",
                                },
                                "start",
                            );
                        }
                        tl.to(
                            item.querySelector(".slideshow__slide-bg"),
                            {
                                duration: 1.5,
                                scale: 1,
                            },
                            "start",
                        );

                        if (item.querySelector(".subheading")) {
                            tl.to(
                                item.querySelector(".subheading"),
                                {
                                    duration: 0.5,
                                    autoAlpha: 1,
                                },
                                0,
                            );

                            button_offset += 0.5;
                        }
                        if (item.querySelector("h1")) {
                            let h1_duration =
                                0.5 +
                                (item.querySelectorAll("h1 .line-child div")
                                    .length -
                                    1) *
                                    0.05;
                            tl.from(
                                item.querySelectorAll("h1 .line-child div"),
                                {
                                    duration: h1_duration,
                                    yPercent: "100",
                                    stagger: 0.05,
                                },
                                0,
                            );
                            button_offset += h1_duration;
                        }
                        if (item.querySelector("p:not(.subheading)")) {
                            let p_duration =
                                0.5 +
                                (item.querySelectorAll(
                                    "p:not(.subheading) .line-child div",
                                ).length -
                                    1) *
                                    0.02;
                            tl.from(
                                item.querySelectorAll(
                                    "p:not(.subheading) .line-child div",
                                ),
                                {
                                    duration: p_duration,
                                    yPercent: "100",
                                    stagger: 0.02,
                                },
                                0,
                            );
                            button_offset += p_duration;
                        }
                        if (item.querySelectorAll(".button")) {
                            tl.fromTo(
                                item.querySelectorAll(".button"),
                                {
                                    y: "100%",
                                },
                                {
                                    duration: 0.5,
                                    y: "0%",
                                    stagger: 0.1,
                                },
                                button_offset * 0.2,
                            );
                        }
                        item.dataset.timeline = tl;
                    });
                slideshow.dataset.animationsReady = true;
            }
        }
        animateSlides(i, animations) {
            const slideshow = this;
            const slide = slideshow.querySelectorAll(".slideshow__slide")[i];
            const hasText = slide.querySelector("h1, p, .subheading, .button");

            if (hasText) {
                document.fonts.ready.then(function () {
                    animations[i].timeScale(1).restart();
                });
            } else {
                animations[i].timeScale(1).restart();
            }
        }
        animateReverse(i, animations) {
            animations[i].timeScale(3).reverse();
        }
        centerArrows(flickity, prev_button, next_button) {
            let first_cell = flickity.cells[0],
                max_height = 0,
                image_height = first_cell.element.querySelector(
                    ".product-featured-image",
                ).clientHeight;

            flickity.cells.forEach((item, i) => {
                if (item.size.height > max_height) {
                    max_height = item.size.height;
                }
            });

            if (max_height > image_height) {
                let difference = (max_height - image_height) / -2;

                prev_button.style.transform =
                    "translateY(" + difference + "px)";
                next_button.style.transform =
                    "translateY(" + difference + "px)";
            }
        }
    }
    customElements.define("slide-show", SlideShow);
}
